import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ClientService } from "../../services/clients/clientService";
import { DashboardCharts } from "../../components/analytics/DashboardCharts";
import { TrendingUp, TrendingDown, Download } from "lucide-react";
import { toast } from "react-hot-toast";
import { exportToCSV } from "../../utils/exportUtils";

export const ClientAnalytics: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const clientId = user?.client_profile_id;
      if (!clientId) { setLoading(false); return; }
      try {
        const response = await ClientService.getClientStats(clientId);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching client stats", error);
        toast.error("Erreur chargement statistiques");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  const fmt = (v: number) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(v);

  const metrics = [
    {
      title: "Factures Totales",
      value: stats?.factures_totales ?? 0,
      change: "",
      trend: "neutral",
      description: "toutes periodes",
    },
    {
      title: "Volume Traite",
      value: stats?.montant_total_traite != null ? fmt(stats.montant_total_traite) : "0 FCFA",
      change: "",
      trend: "neutral",
      description: "factures approuvees",
    },
    {
      title: "Fournisseurs Actifs",
      value: stats?.nombre_fournisseurs_actifs ?? 0,
      change: "",
      trend: "neutral",
      description: "partenaires",
    },
    {
      title: "Delai Moyen (Jours)",
      value: stats?.delai_moyen_paiement ?? 0,
      change: "",
      trend: "neutral",
      description: "echeance vs emission",
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-600">
            Tableaux de bord et indicateurs de performance
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button 
            onClick={() => exportToCSV(metrics, "client_analytics_metrics")}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </button>
        </div>
      </div>

      {/* Métriques clés */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {metric.trend === "up" ? (
                    <div className="p-3 rounded-lg bg-success-100">
                      <TrendingUp className="h-6 w-6 text-success-600" />
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-primary-100">
                      <TrendingDown className="h-6 w-6 text-primary-600" />
                    </div>
                  )}
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {metric.title}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {metric.value}
                      </div>
                      <div
                        className={`ml-2 flex items-baseline text-sm font-semibold ${
                          metric.trend === "up"
                            ? "text-success-600"
                            : "text-primary-600"
                        }`}
                      >
                        {metric.change}
                      </div>
                    </dd>
                    <dt className="text-xs text-gray-500 mt-1">
                      {metric.description}
                    </dt>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Graphiques */}
      <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Activité Financière
          </h3>
          <DashboardCharts />
      </div>

      {/* Insights Placeholder - could be dynamic later */}
       <div className="bg-white shadow rounded-lg p-6">
           <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Recommandations IA
          </h3>
          <div className="space-y-4">
               <div className="flex items-start">
                   <div className="flex-shrink-0">
                        <div className="p-2 bg-primary-100 rounded-full">
                            <TrendingUp className="h-4 w-4 text-primary-600" />
                        </div>
                   </div>
                   <div className="ml-3">
                       <p className="text-sm font-medium text-gray-900">Optimisation de Trésorerie</p>
                       <p className="text-sm text-gray-600">Basé sur vos délais de paiement actuels, vous pourriez économiser en négociant des escomptes pour paiement anticipé.</p>
                   </div>
               </div>
          </div>
       </div>

    </div>
  );
};
